import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  ANNOTATION_KIND_SECTION,
  highestReadyLayer,
  SectionViewer,
  CMX_DOCUMENT_CONTENT_BODY_LAYER,
  type CmxDocumentContent,
  CmxDocumentContentBody,
  toViewAnnotations,
  useCmxDocumentContent,
  useCmxDocumentContentStatus
} from '@codametrix/shared-views';
import React from 'react';

import type { SlotProps } from '../../../project_starburst';
import {
  DOCUMENT_ID_PARAM,
  resolveDocumentId,
  resolveRequestedLayer
} from './cmxDocumentContentSlot';

export type {
  CmxDocumentContentSlotData,
  CmxDocumentContentSlotEntry
} from './cmxDocumentContentSlot';

export type CmxDocumentContentContentProps = {
  readonly documentId: string | null;
  readonly layer: number;
  readonly document: CmxDocumentContent | null;
  readonly loading: boolean;
  readonly error: Error | null;
  /**
   * Status came back and NO layer is retrievable. Distinct from `error` (the status read itself
   * failed) and from a null `document` (a layer was read and returned nothing), because the
   * honest thing to tell a coder differs in each case -- and because fetching a layer that status
   * says is `pending`/`notRun` would 404 in a way indistinguishable from a missing document.
   */
  readonly noLayerReady?: boolean;
};

/**
 * Every rendered state of a cmx-document-content read, and no fetching.
 *
 * Split from the slot below so the states are drivable from a story and a test without mocking a
 * hook or a transport -- the read result is just props.
 *
 * Sectioned vs flat is decided by the RESPONSE, not by configuration. The service serves
 * `layer: null` throughout this increment (an annotation payload may not cite a body below tier
 * 3), so this renders the flat body until something produces sections -- and switches to the
 * sectioned viewer the moment a payload carrying `section` annotations arrives.
 */
export const CmxDocumentContentContent: React.FC<
  CmxDocumentContentContentProps
> = ({
  documentId,
  layer,
  document: cmxDocumentContent,
  loading,
  error,
  noLayerReady = false
}) => {
  if (!documentId) {
    return (
      <Flex
        role="region"
        aria-label="CMX Document Content viewer"
        data-testid="cmx-document-content-viewer-unaddressed"
        align="center"
        justify="center"
        p={4}
      >
        <Text color="gray.500">
          No CMX Document Content for this case. Append{' '}
          <Text as="code">?{DOCUMENT_ID_PARAM}=&lt;id&gt;</Text> to address one.
        </Text>
      </Flex>
    );
  }

  if (loading) {
    return (
      <Flex
        role="region"
        aria-label="CMX Document Content viewer"
        data-testid="cmx-document-content-viewer-loading"
        align="center"
        justify="center"
        p={4}
        gap={3}
      >
        <Spinner size="sm" />
        <Text color="gray.500">Loading CMX Document Content…</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        role="region"
        aria-label="CMX Document Content viewer"
        data-testid="cmx-document-content-viewer-error"
        align="center"
        justify="center"
        p={4}
      >
        <Text color="red.600">
          Could not load layer {layer} of {documentId}: {error.message}
        </Text>
      </Flex>
    );
  }

  if (noLayerReady) {
    return (
      <Flex
        role="region"
        aria-label="CMX Document Content viewer"
        data-testid="cmx-document-content-viewer-no-layer"
        align="center"
        justify="center"
        p={4}
      >
        <Text color="gray.500">
          No layer of this CMX Document Content is ready yet.
        </Text>
      </Flex>
    );
  }

  if (!cmxDocumentContent) {
    return (
      <Flex
        role="region"
        aria-label="CMX Document Content viewer"
        data-testid="cmx-document-content-viewer-empty"
        align="center"
        justify="center"
        p={4}
      >
        <Text color="gray.500">No CMX Document Content available</Text>
      </Flex>
    );
  }

  const annotations = toViewAnnotations(cmxDocumentContent.layer);
  const hasSections = annotations.some(
    (annotation) => annotation.kind === ANNOTATION_KIND_SECTION
  );

  return (
    <Box
      role="region"
      aria-label="CMX Document Content viewer"
      data-testid="cmx-document-content-viewer"
      data-body-tier={cmxDocumentContent.body.tier}
      data-body-media-type={cmxDocumentContent.body.mediaType}
      data-layer={layer}
    >
      <Box
        data-testid="cmx-document-content-viewer-body"
        p={4}
        overflowX="auto"
        sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        {hasSections ? (
          <SectionViewer
            body={cmxDocumentContent.body.text}
            annotations={annotations}
            mediaType={cmxDocumentContent.body.mediaType}
          />
        ) : (
          <CmxDocumentContentBody
            text={cmxDocumentContent.body.text}
            annotations={annotations}
            mediaType={cmxDocumentContent.body.mediaType}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * LEFT-pane cmx-document-content body -- the Markdown rendering of a CMX Document Content.
 *
 * Calls `GET /documents/cmx-document-content/{cmxDocumentContentId}/layers/{layer}/v1` for real, through the same
 * `requestJSON` every other read in this adapter uses, so path resolution, bearer/session auth and
 * CSRF all behave exactly as they will in production. There is deliberately no mock transport: the
 * response is whatever the service returns, or whatever is intercepted in the browser.
 *
 * Props are `Partial<SlotProps>` so this works from BOTH entry points: as a registered slot (given
 * the full `SlotProps`), and rendered directly by `NoteViewer` with no props when the
 * cmx-document-content entry on the NoteNav strip is the active one.
 */
export const CmxDocumentContentViewer: React.FC<Partial<SlotProps>> = ({
  value
}) => {
  const documentId = resolveDocumentId(value);
  const requestedLayer = resolveRequestedLayer(value);

  const {
    status,
    loading: statusLoading,
    error: statusError
  } = useCmxDocumentContentStatus(requestedLayer === null ? documentId : null);

  const readyLayer = highestReadyLayer(status);
  const resolvedLayer = requestedLayer ?? readyLayer;

  const {
    document: cmxDocumentContent,
    loading: bodyLoading,
    error: bodyError
  } = useCmxDocumentContent(
    resolvedLayer === null ? null : documentId,
    resolvedLayer ?? CMX_DOCUMENT_CONTENT_BODY_LAYER
  );

  return (
    <CmxDocumentContentContent
      documentId={documentId}
      layer={resolvedLayer ?? CMX_DOCUMENT_CONTENT_BODY_LAYER}
      document={cmxDocumentContent}
      loading={statusLoading || bodyLoading}
      error={statusError ?? bodyError}
      noLayerReady={
        requestedLayer === null &&
        !statusLoading &&
        statusError === null &&
        status !== null &&
        readyLayer === null
      }
    />
  );
};
